/**
 * The six-figure summary of a client's contract history, shown to administrators at the
 * bottom of the Contracts page.
 *
 * The definitions are not invented: they were read back from the legacy `clientstats`
 * table, which stores exactly these figures per client, and checked against the contracts
 * they were computed from.
 *
 * - **Count and value** cover *every* contract, whatever its status. The figures are shown
 *   in **RON** (`loan_amount` -> `SumainLei`), the currency the shop's own books are in.
 *   The legacy table stored the same aggregates in euro (`loan_amount_eur`), which is how
 *   the definitions below were verified against it to the cent.
 * - **Expired count and value** cover the contracts the client did not get back:
 *   `'!!'` (expired), `'INV'` (taken into stock) and `'EXP'` (sold on). Verified against
 *   several stored rows, including ones where the `INV` contract is what makes the count
 *   add up.
 * - **Net profit** is the interest earned on redeemed contracts:
 *   `loan x daily rate% x duration`, summed over `'OK'`, in RON. `daily_interest_rate` is a
 *   percentage per day (0.5 on 305k of the 314k contracts), hence the `/100`. This is the
 *   one figure that does not reproduce the stored value exactly — 891.49 against a stored
 *   883.41 on the client used to derive it, i.e. within 1%, the legacy formula having
 *   presumably carried an adjustment that is no longer recoverable (the sales table is
 *   empty and holds no amounts). It is computed live here rather than read from
 *   `clientstats`, which was last written on 2026-02-10 and covers 11,387 of 31,900
 *   clients.
 */

/**
 * A figure as the API hands it over: numeric in the database, but decimals arrive as strings,
 * and a column that was never filled in arrives as null.
 */
export type NumericField = number | string | null;

/** Contract fields the summary needs; all of them arrive on the page's contract rows. */
export interface SummaryContract {
    Statut?: string | null;
    /** `loan_amount`, in RON. */
    SumainLei?: NumericField;
    /** `daily_interest_rate`, a percentage per day. */
    Comision_zi?: NumericField;
    /** `duration_days`. */
    Durata?: NumericField;
}

export interface ClientContractSummary {
    /** Every contract the client has. */
    contractCount: number;
    /** Their total value, in RON. */
    contractValue: number;
    /** Contracts that ended without the client redeeming them. */
    expiredCount: number;
    /** Their total value, in RON. */
    expiredValue: number;
    /** Interest earned on the contracts the client redeemed, in RON. */
    netProfit: number;
}

/** Statuses of a contract the client did not redeem: expired, taken into stock, sold. */
export const EXPIRED_SUMMARY_STATUSES: readonly string[] = ['!!', 'INV', 'EXP'];

/** Status of a redeemed contract — the only one that earns interest. */
export const REDEEMED_STATUS = 'OK';

/** Read a numeric field that may arrive as a string, treating anything unusable as 0. */
const toNumber = (value: NumericField | undefined): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const isExpired = (contract: SummaryContract): boolean =>
    !!contract.Statut && EXPIRED_SUMMARY_STATUSES.includes(contract.Statut);

/** Interest earned on one redeemed contract: loan x daily rate% x days. */
const interestEarned = (contract: SummaryContract): number =>
    contract.Statut === REDEEMED_STATUS
        ? (toNumber(contract.SumainLei) *
              toNumber(contract.Comision_zi) *
              toNumber(contract.Durata)) /
          100
        : 0;

/** Summarise a client's contracts. An empty list is a valid answer: six zeros. */
export const summariseClientContracts = (
    contracts: readonly SummaryContract[],
): ClientContractSummary =>
    contracts.reduce<ClientContractSummary>(
        (summary, contract) => {
            const value = toNumber(contract.SumainLei);
            const expired = isExpired(contract);
            return {
                contractCount: summary.contractCount + 1,
                contractValue: summary.contractValue + value,
                expiredCount: summary.expiredCount + (expired ? 1 : 0),
                expiredValue: summary.expiredValue + (expired ? value : 0),
                netProfit: summary.netProfit + interestEarned(contract),
            };
        },
        {
            contractCount: 0,
            contractValue: 0,
            expiredCount: 0,
            expiredValue: 0,
            netProfit: 0,
        },
    );
